import React, { useState, useMemo, useEffect } from "react";
import { Tree, Input, Typography, Space } from "antd";
import {
  CaretDownOutlined,
  DeleteOutlined,
  HolderOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import "../style.scss";
import { IPositionGroup, IVConditionFilterV2 } from "../../../../api/groupCustomerV2/groupCustomerV2Api";
import { user_double } from "../../../../components/ExportImgFigma";
import { formatPrice } from "../../../../until";
import { useSelector } from "react-redux";
import { selectorInfoUser } from "../../../../store/slices/infoUserSlice";

const { Text } = Typography;

export interface TreeNode {
  key: string;
  title: React.ReactNode;
  level: number;
  customerCount?: number; // 👈 thêm count ở đây
  children?: TreeNode[];
  parentId?: number;
  isDel?: boolean
}

export interface ITreeNodeClone {
  parentId?: number;
  level?: number;
  status: number
  type: number
  title: string
  description?: string
  infoFilter?: IVConditionFilterV2
}

export type NodeEvent = {
  action: "add" | "update" | "view" | "delete" | "combine" | "clone";
  item?: TreeNode;
  dataClone?: ITreeNodeClone
};

interface props {
  add: boolean,
  edit: boolean,
  isDelete: boolean,
  data: TreeNode[];
  inforNodeEvent?: NodeEvent;
  setInforNodeEvent: (e: NodeEvent) => void;
  onChangePositionGroup?: (positionGroup: IPositionGroup[]) => void
}

const normalizeTree = (nodes?: TreeNode[]): TreeNode[] => {
  if (!nodes) return [];
  return nodes.map((n) => {
    const normalized: TreeNode = {
      ...n,
      key: String(n.key),
      title: n.title,
      level: n.level,
      parentId: n.parentId,
      children: n.children ? normalizeTree(n.children) : undefined,
    };
    return normalized;
  });
};

const Sidebar: React.FC<props> = ({
  add, edit, isDelete,
  data, inforNodeEvent,
  setInforNodeEvent, onChangePositionGroup
}) => {

  const { id } = useSelector(selectorInfoUser)

  const [search, setSearch] = useState("");
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [treeData, setTreeData] = useState<TreeNode[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [addingKey, setAddingKey] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState<string>("");


  // Tìm tất cả ancestor keys (từ root đến parent của target)
  const findAncestorKeys = (nodes: TreeNode[], targetKey: string, acc: string[] = []): string[] | null => {
    for (const n of nodes) {
      if (n.key === targetKey) {
        return acc; // Trả về danh sách ancestors
      }
      if (n.children) {
        const res = findAncestorKeys(n.children, targetKey, [...acc, n.key]);
        if (res) return res;
      }
    }
    return null;
  };

  // Tìm node object theo key
  const findNodeByKey = (nodes: TreeNode[], key: string): TreeNode | null => {
    for (const n of nodes) {
      if (n.key === key) return n;
      if (n.children) {
        const c = findNodeByKey(n.children, key);
        if (c) return c;
      }
    }
    return null;
  };

  // Lấy text từ ReactNode
  const getNodeText = (node: React.ReactNode): string => {
    if (typeof node === "string") return node;
    if (typeof node === "number") return node.toString();
    if (Array.isArray(node)) return node.map(getNodeText).join(" ");
    if (React.isValidElement(node)) return getNodeText((node.props as any).children);
    return "";
  };



  // Remove node helper
  const removeNode = (nodes: TreeNode[], key: string): [TreeNode | null, TreeNode[]] => {
    const result: TreeNode[] = [];
    let removed: TreeNode | null = null;
    nodes.forEach((n) => {
      if (n.key === key) {
        removed = n;
        return;
      }
      if (n.children) {
        const [childRemoved, newChildren] = removeNode(n.children, key);
        if (childRemoved) removed = childRemoved;
        result.push({ ...n, children: newChildren });
      } else {
        result.push(n);
      }
    });
    return [removed, result];
  };

  // Insert node helper (used by drag/drop)
  const insertNode = (
    nodes: TreeNode[],
    key: string,
    node: TreeNode,
    dropPosition: number,
    dropToGap: boolean
  ): TreeNode[] => {
    return nodes.map((n) => {
      if (n.key === key) {
        if (!dropToGap) {
          const children = n.children ? [...n.children, node] : [node];
          return { ...n, children };
        } else {
          return n;
        }
      }
      if (n.children) {
        return {
          ...n,
          children: insertNode(n.children, key, node, dropPosition, dropToGap),
        };
      }
      return n;
    });
  };

  // Hàm duyệt đệ quy toàn bộ cây để lấy ra tất cả key
  // type IdOnlyNode = { id: number; children?: IdOnlyNode[] };

  const toIdOnlyTree = (nodes: TreeNode[]): IPositionGroup[] =>
    nodes.map((n) => {
      const item: IPositionGroup = { id: Number(n.key) };
      if (n.children && n.children.length) {
        item.children = toIdOnlyTree(n.children);
      }
      return item;
    });

  const onDrop = (info: any) => {
    const dropKey = info.node.key;
    const dragKey = info.dragNode.key;
    const dropPos = info.node.pos.split('-');
    const dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1]); // the drop position relative to the drop node, inside 0, top -1, bottom 1

    const loop = (
      data: TreeNode[],
      key: React.Key,
      callback: (node: TreeNode, i: number, data: TreeNode[]) => void,
    ) => {
      for (let i = 0; i < data.length; i++) {
        if (data[i].key === key) {
          return callback(data[i], i, data);
        }
        if (data[i].children) {
          loop(data[i].children!, key, callback);
        }
      }
    };
    const data = [...treeData];

    // Find dragObject
    let dragObj: TreeNode;
    loop(data, dragKey, (item, index, arr) => {
      arr.splice(index, 1);
      dragObj = item;
    });

    if (!info.dropToGap) {
      // Drop on the content
      loop(data, dropKey, (item) => {
        item.children = item.children || [];
        // where to insert. New item was inserted to the start of the array in this example, but can be anywhere
        item.children.unshift(dragObj);
      });
    } else {
      let ar: TreeNode[] = [];
      let i: number;
      loop(data, dropKey, (_item, index, arr) => {
        ar = arr;
        i = index;
      });
      if (dropPosition === -1) {
        // Drop on the top of the drop node
        ar.splice(i!, 0, dragObj!);
      } else {
        // Drop on the bottom of the drop node
        ar.splice(i! + 1, 0, dragObj!);
      }
    }
    setTreeData(data);
    const positionGroup = toIdOnlyTree(data);
    onChangePositionGroup?.(positionGroup)
  };

  // Search filter
  const filteredData = useMemo(() => {
    if (!search.trim()) return treeData;
    const q = search.toLowerCase();
    const expandKeys: string[] = [];

    const dfs = (nodes: TreeNode[], parentKeys: string[] = []): TreeNode[] => {
      const res: TreeNode[] = [];
      nodes.forEach((n) => {
        const text = getNodeText(n.title).toLowerCase();
        const hit = text.includes(q);
        const children = n.children
          ? dfs(n.children, [...parentKeys, n.key])
          : [];
        if (hit || children.length) {
          if (hit) expandKeys.push(...parentKeys);
          if (children.length) expandKeys.push(n.key);
          res.push({ ...n, children });
        }
      });
      return res;
    };

    const data = dfs(treeData);
    setExpandedKeys((prev) => [...new Set([...prev, ...expandKeys])]);
    return data;
  }, [search, treeData]);

  // Thêm node con
  const addChildNode = (parentKey: string, title: string) => {
    if (!title.trim()) return;

    const newNode: TreeNode = {
      key: `${parentKey}-${Date.now()}`,
      title: title.trim(),
      customerCount: (findNodeByKey(treeData, parentKey)?.customerCount || 0),
      level: (findNodeByKey(treeData, parentKey)?.level || 0) + 1,
      isDel: true
    };

    const addChild = (nodes: TreeNode[]): TreeNode[] =>
      nodes.map((n) => {
        if (n.key === parentKey) {
          return {
            ...n,
            children: [...(n.children || []), newNode],
          };
        }
        if (n.children) {
          return { ...n, children: addChild(n.children) };
        }
        return n;
      });

    setTreeData((prev) => addChild(prev));
    setAddingKey(null);
    setNewTitle("");
  };

  // Thêm node root
  const addRootNode = (title: string) => {
    if (!title.trim()) return;

    const newNode: TreeNode = {
      key: `root-${Date.now()}`,
      title: title.trim(),
      level: 1,
      isDel: true
    };

    setTreeData((prev) => [...prev, newNode]);
    setNewTitle("");
  };

  // Xử lý khi có inforNodeEvent thay đổi
  useEffect(() => {
    if (!inforNodeEvent?.item?.key) return;

    const targetKey = String(inforNodeEvent.item.key);

    if (inforNodeEvent.action === "combine" || inforNodeEvent.action === "add") {
      // Tìm ancestors dựa trên treeData
      const ancestors = findAncestorKeys(treeData, targetKey) || [];

      // Mở ancestors + chính node target
      setExpandedKeys((prev) => [...new Set([...prev, ...ancestors, targetKey])]);

      // Show input dưới node target
      setAddingKey(targetKey);

      // Set newTitle từ item
      const titleText = typeof inforNodeEvent.item.title === "string"
        ? inforNodeEvent.item.title
        : getNodeText(inforNodeEvent.item.title);
      setNewTitle(titleText || "");
    }
  }, [inforNodeEvent, treeData]);

  useEffect(() => {
    setTreeData(normalizeTree(data));
  }, [data]);

    useEffect(() => {
    setSelectedKey(null)
  }, [inforNodeEvent?.action]);
  return (
    <Space direction="vertical" style={{ width: "100%" }} size="small">
      {/* Nút thêm nhóm mới (root) */}
      <Space
        style={{ color: "#1677ff", cursor: "pointer", userSelect: "none" }}
        className={`${!add && 'disabled'}`}
        onClick={() => {
          setInforNodeEvent({ action: "add", item: undefined });
          setAddingKey(null);
          setSelectedKey(null);
        }}
      >
        <PlusCircleOutlined />
        <span className="custom-page-css text-base font-medium whitespace-nowrap">Thêm nhóm mới</span>
      </Space>
      {/* Input cho thêm root */}
      {inforNodeEvent?.action === 'add' && !inforNodeEvent?.item && (
        <Input
          disabled
          size="small"
          className="custom-page-css border-none bg-EBEBEB"
          placeholder="Tên nhóm"
          autoFocus
          onChange={(e) => setNewTitle(e.target.value)}
          onPressEnter={() => addRootNode(newTitle)}
          onBlur={() => {
            addRootNode(newTitle);
            setInforNodeEvent({ action: "view" });
          }}
        />
      )}
      <div className="tree-vertical-only tree-vertical-only-custom handles-column custom-tree-sideBar">
        <Tree
          blockNode
          showLine={{ showLeafIcon: false }}
          switcherIcon={<CaretDownOutlined />}
          draggable={{ icon: <HolderOutlined className="tree-handle" /> }}
          onDrop={onDrop}
          treeData={filteredData}
          expandedKeys={expandedKeys}
          onExpand={(keys) => setExpandedKeys(keys)}
          selectable={edit}
          selectedKeys={
            selectedKey
              ? [selectedKey]
              : (inforNodeEvent?.item?.key && inforNodeEvent.action === "view")
                ? [String(inforNodeEvent.item.key)]
                : []
          }
          titleRender={(node) => {
            const hasChildren = Array.isArray(node.children) && node.children.length > 0;
            const nodeKeyStr = String(node.key);

            // isSelected: highlight span (khi click span)
            const isSelected = (selectedKey === nodeKeyStr) ||
              ((nodeKeyStr === inforNodeEvent?.item?.key) && inforNodeEvent.action === "view");
            // isPlusActive: highlight only plus icon (khi click +)
            const isPlusActive = addingKey === nodeKeyStr && (inforNodeEvent?.action === "add" || inforNodeEvent?.action === "combine");

            return (
              <div className="" style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div
                    className={`${!edit && 'event-none-click'} custom-page-css flex flex-col gap-1 `}
                    onClick={() => {
                      // click span => highlight span only
                      setInforNodeEvent({ action: "view", item: node });
                      setSelectedKey(nodeKeyStr);
                      // clear addingKey so plus icon not highlighted
                      setAddingKey(null);
                    }}
                  >
                    <span
                      className={`${hasChildren ? "font-medium" : ""} ${isSelected ? "custom-page-css text-096DD9" : ""}`}

                      style={{ cursor: "pointer" }}
                    >
                      {typeof node.title === "string" ? node.title : node.title}
                    </span>
                    {/* <span className="custom-page-css flex items-center gap-1">
                      <img src={user_double} alt="" />
                      <span className="custom-page-css text-xs text-667085 font-regular">{formatPrice(node.customerCount)}</span>
                    </span> */}

                  </div>
                  {/* Plus icon: highlight when isPlusActive */}
                  <div className="custom-page-css flex gap-2">
                    <PlusCircleOutlined
                      className={`${!add && 'disabled'}`}
                      style={{
                        color: isPlusActive ? "#096DD9" : (hasChildren ? "#101828" : "#98A2B3"),
                        cursor: "pointer",
                      }}
                      onClick={(e) => {
                        e.stopPropagation(); // ngăn event bubble lên span
                        setAddingKey(nodeKeyStr);
                        setExpandedKeys((prev) => [...new Set([...prev, nodeKeyStr])]);
                        // DON'T set selectedKey here — leave span un-highlighted
                        setSelectedKey(null);
                        setInforNodeEvent({ action: "add", item: node });
                      }}
                    />
                    <DeleteOutlined
                      className={
                        id !== 1 && (!isDelete || !node.isDel)
                          ? 'disabled'
                          : ''
                      }
                      style={{ color: "#F5222D", cursor: "pointer" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setInforNodeEvent({ action: "delete", item: node });
                      }}
                    />
                  </div>
                </div>

                {/* Input hiển thị khi đang add/combine under this node */}
                {String(addingKey) === nodeKeyStr && (inforNodeEvent?.action === "add" || inforNodeEvent?.action === "combine") && (
                  <Input
                    disabled
                    size="small"
                    className="custom-page-css border-none bg-EBEBEB"
                    placeholder="Tên nhóm"
                    autoFocus
                    onChange={(e) => setNewTitle(e.target.value)}
                    onPressEnter={() => {
                      if (inforNodeEvent?.action === "combine") {
                        // console.log("Combine node:", node, "with title:", newTitle);
                      } else {
                        addChildNode(nodeKeyStr, newTitle);
                      }
                    }}
                    onBlur={() => {
                      if (inforNodeEvent?.action === "combine") {
                        // console.log("Combine on blur:", node, "with title:", newTitle);
                      } else {
                        addChildNode(nodeKeyStr, newTitle);
                      }
                      setAddingKey(null);
                    }}
                  />
                )}
              </div>
            );
          }}

        />
      </div>




    </Space>
  );
};

export default React.memo(Sidebar);
